import { useState } from "react";
import {
  Anchor,
  Building2,
  Container as ContainerIcon,
  Download,
  Eye,
  Factory,
  FileText,
  Map as MapIcon,
  Paperclip,
  ShieldCheck,
  Signpost,
  Wallet,
  Wrench,
} from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import { SectionCard } from "../UI/SectionCard";
import Button from "../UI/Button";
// The reading half of this module, shared with the container's own read view.
import { Field, FieldGrid, ReadonlyStopCard } from "./readonlyFields";
import { EMPTY, formatAddress, formatDate } from "./readonlyFormat";
import { containerOrderAPI } from "../../services/api";
import {
  CONTAINER_TYPE_OPTIONS,
} from "../../constants/containerOrderConstants";
import { PreviewableExtensions } from "../../constants/formOptions";
import { listHasTax, sumTax, taxCellLabel } from "../../utils/tax";
import { deliverAttachment } from "../../utils/containerAttachments";
import type {
  ContainerOrder,
  ContainerOrderAttachment,
} from "../../types/containerOrders";
import type { ServiceSnapshot } from "../../types";

interface ContainerOrderReadonlyProps {
  order: ContainerOrder;
}

/** Yes/no as a chip — quicker to scan down a column than the words. */
function Flag({ label, on }: { label: string; on?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        on
          ? "border-primary-200 bg-primary-50 text-primary-700"
          : "border-[rgba(11,15,14,0.08)] text-secondary-400"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${on ? "bg-primary" : "bg-secondary-300"}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

/**
 * A saved container order, laid out for reading.
 *
 * This is not the form with its inputs disabled. A disabled form still costs
 * the reader a box, a border and a placeholder per value, and buries the three
 * things they actually came for — who, where, and how far along — in the same
 * visual weight as the ISO code. Here the data is the layout: definition grids,
 * the route as three ordered stops, and money in real tables.
 */
export function ContainerOrderReadonly({ order }: ContainerOrderReadonlyProps) {
  const { t } = useTranslation(["containerOrders", "shipments", "common"]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const containerTypeLabel =
    CONTAINER_TYPE_OPTIONS.find((o) => o.value === order.containerType)?.label ??
    order.containerType;

  const isPreviewable = (name: string) =>
    PreviewableExtensions.includes(name.split(".").pop()?.toLowerCase() ?? "");

  const handleDownload = async (
    attachment: ContainerOrderAttachment,
    previewOnly = false,
  ) => {
    try {
      setBusyId(attachment._id);
      const res = await containerOrderAPI.downloadAttachment(
        order._id,
        attachment._id,
      );
      deliverAttachment(res, attachment, previewOnly);
    } catch {
      toast.error(t("attachments.downloadFailed"));
    } finally {
      setBusyId(null);
    }
  };

  // ── Service tables ─────────────────────────────────────────────────────────
  const renderServices = (rows: ServiceSnapshot[] = []) => {
    if (!rows.length) {
      return (
        <p className="text-[12px] text-secondary-400">
          {t("readonly.noServices")}
        </p>
      );
    }

    const withTotals = rows.map((row) => ({
      ...row,
      total: (row.qty || 0) * (row.price || 0),
    }));
    const net = withTotals.reduce((sum, row) => sum + row.total, 0);
    const hasTax = listHasTax(withTotals);
    const tax = sumTax(withTotals);

    const th =
      "px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-mist";
    const td = "px-2 py-1.5 align-top text-[12px] text-ink";

    return (
      <div className="overflow-hidden rounded-md border border-[rgba(11,15,14,0.08)]">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-[rgba(11,15,14,0.08)] bg-bone">
              <tr>
                <th className={`${th} min-w-40`}>
                  {t("shipments:services.serviceCode")}
                </th>
                <th className={`${th} min-w-40`}>
                  {t("shipments:services.description")}
                </th>
                <th className={th}>{t("shipments:services.unitType")}</th>
                <th className={`${th} text-right`}>
                  {t("shipments:services.price")}
                </th>
                <th className={`${th} text-right`}>
                  {t("shipments:services.qty")}
                </th>
                <th className={`${th} text-right`}>
                  {t("shipments:services.tax")}
                </th>
                <th className={`${th} text-right`}>
                  {t("shipments:services.totalExcludingTax")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(11,15,14,0.06)] bg-white">
              {withTotals.map((row, index) => (
                <tr key={row._id ?? index}>
                  <td className={`${td} font-medium`}>{row.name || EMPTY}</td>
                  <td className={td}>{row.description || EMPTY}</td>
                  <td className={td}>{row.type || EMPTY}</td>
                  <td className={`${td} text-right`}>
                    €{Number(row.price || 0).toFixed(2)}
                  </td>
                  <td className={`${td} text-right`}>{row.qty || 0}</td>
                  <td className={`${td} text-right text-secondary-600`}>
                    {taxCellLabel(row.total, row.tax)}
                  </td>
                  <td className={`${td} text-right font-semibold`}>
                    €{row.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-1 border-t border-[rgba(11,15,14,0.08)] bg-bone px-3 py-2 text-[12px]">
          <span>
            <span className="text-mist">
              {t("shipments:services.totalPrice")}
            </span>{" "}
            <span className="font-semibold text-ink">€{net.toFixed(2)}</span>
          </span>
          {hasTax && (
            <>
              <span>
                <span className="text-mist">
                  {t("shipments:services.taxTotal")}
                </span>{" "}
                <span className="font-semibold text-ink">
                  €{tax.toFixed(2)}
                </span>
              </span>
              <span>
                <span className="text-mist">
                  {t("shipments:services.totalWithTax")}
                </span>{" "}
                <span className="font-semibold text-ink">
                  €{(net + tax).toFixed(2)}
                </span>
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Route stop ─────────────────────────────────────────────────────────────
  // Positional wrapper over the shared card, so the three call sites below stay
  // as they were.
  const renderStop = (
    index: number,
    icon: typeof Building2,
    title: string,
    kind: string,
    accent: string,
    companyName?: string,
    address?: Parameters<typeof formatAddress>[0],
    rows?: Array<{ label: string; value?: React.ReactNode }>,
    /**
     * A stop the order has marked as not being made.
     *
     * When set, the card is dimmed and shows the reason instead of the stop's
     * details — there is no truck going there, and the detail rows would
     * describe a visit nobody is making. The reason is what the reader actually
     * needs, and it is what every container generated from this order will show
     * its driver.
     */
    skipReason?: string,
  ) => (
    <ReadonlyStopCard
      index={index}
      icon={icon}
      title={title}
      kind={kind}
      accent={skipReason === undefined ? accent : "bg-secondary-300"}
      muted={skipReason !== undefined}
      companyName={companyName}
      address={address}
      rows={
        skipReason === undefined
          ? rows
          : [{ label: t("route.skipReason"), value: skipReason || EMPTY }]
      }
    />
  );

  /** `undefined` when the stop is being made — see `renderStop`. */
  const skipOf = (stop?: { enabled?: boolean; skipReason?: string }) =>
    stop?.enabled === false ? (stop.skipReason ?? "") : undefined;

  const attachmentsByCategory = (category: "general" | "customs") =>
    (order.attachments ?? []).filter(
      (a) => (a.category ?? "general") === category,
    );

  const renderAttachments = (
    label: string,
    Icon: typeof Paperclip,
    items: ContainerOrderAttachment[],
  ) => (
    <div>
      <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-mist">
        <Icon className="h-3.5 w-3.5" />
        {label}
        <span className="text-secondary-400">({items.length})</span>
      </p>

      {items.length === 0 ? (
        <p className="text-[12px] text-secondary-300">
          {t("readonly.noAttachments")}
        </p>
      ) : (
        <ul className="space-y-1">
          {items.map((attachment) => (
            <li
              key={attachment._id}
              className="flex items-center gap-2 rounded-md border border-[rgba(11,15,14,0.08)] bg-white px-2.5 py-1.5"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-mist" />
              <span className="min-w-0 flex-1 truncate text-[12px] text-ink">
                {attachment.originalName}
              </span>
              {isPreviewable(attachment.originalName) && (
                <Button
                  type="button"
                  variant="ghost"
                  className="p-1"
                  disabled={busyId === attachment._id}
                  onClick={() => handleDownload(attachment, true)}
                  title={t("attachments.preview")}
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                className="p-1"
                disabled={busyId === attachment._id}
                onClick={() => handleDownload(attachment)}
                title={t("attachments.download")}
              >
                <Download className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const route = order.route ?? {};
  const pricing = order.pricing;
  const handling = order.handling;

  return (
    <div className="space-y-3">
      {/* ── 1. General information ─────────────────────────────────────── */}
      <SectionCard icon={FileText} title={t("sections.generalInformation")}>
        <FieldGrid>
          <Field label={t("labels.reference")} value={order.referenceNumber} mono />
          <Field
            label={t("labels.bookingNumber")}
            value={order.contractNumber}
            mono
          />
          <Field
            label={t("labels.client")}
            value={order.client?.companyName}
            wide
          />
          <Field
            label={t("shipments:labels.regCode")}
            value={order.client?.registerationCode}
          />
          <Field
            label={t("shipments:labels.phone")}
            value={order.client?.phoneNumber}
          />
          <Field
            label={t("shipments:labels.email")}
            value={order.client?.emailAddress}
          />
          <Field
            label={t("shipments:labels.address")}
            value={formatAddress(order.client?.address)}
            wide
          />
          <Field
            label={t("labels.incotermPlace")}
            value={
              [order.incoterm, order.place].filter(Boolean).join(" ") ||
              undefined
            }
          />
          <Field
            label={t("labels.dateRange")}
            value={
              formatDate(order.dateFrom) || formatDate(order.dateTo)
                ? `${formatDate(order.dateFrom) ?? EMPTY} → ${formatDate(order.dateTo) ?? EMPTY}`
                : undefined
            }
          />
        </FieldGrid>
      </SectionCard>

      {/* ── 2. Container defaults ──────────────────────────────────────── */}
      <SectionCard icon={ContainerIcon} title={t("sections.containerDefaults")}>
        <FieldGrid>
          <Field label={t("labels.containerType")} value={containerTypeLabel} />
          <Field label={t("labels.shippingLine")} value={order.shippingLine} />
          <Field
            label={t("labels.vesselVoyage")}
            value={
              [order.vesselName, order.voyageNumber]
                .filter(Boolean)
                .join(" / ") || undefined
            }
          />
          <Field label={t("labels.blNumber")} value={order.blNumber} mono />
          <Field label={t("labels.isoCode")} value={order.isoCode} mono />
          <Field
            label={t("labels.defaultTare")}
            value={
              order.defaultTareKg != null ? `${order.defaultTareKg} kg` : undefined
            }
          />
          <Field
            label={t("labels.defaultMaxPayload")}
            value={
              order.defaultMaxPayloadKg != null
                ? `${order.defaultMaxPayloadKg} kg`
                : undefined
            }
          />

          <Field
            label={t("labels.handling")}
            wide
            value={
              <span className="flex flex-wrap gap-1.5">
                <Flag
                  label={t("handling.temperature")}
                  on={handling?.temperatureSensitive}
                />
                <Flag label={t("handling.adr")} on={handling?.adr} />
                <Flag label={t("handling.fragile")} on={handling?.fragile} />
              </span>
            }
          />

          {/* Each detail appears only while its flag is on — same rule as the
              form, so the two views never disagree about what is set. */}
          {handling?.temperatureSensitive && (
            <Field
              label={t("labels.tempRange")}
              value={handling.temperatureRange}
            />
          )}
          {handling?.adr && (
            <Field label={t("labels.imoClass")} value={handling.imoClass} />
          )}
          {handling?.fragile && (
            <Field
              label={t("labels.specifications")}
              value={handling.specifications}
              wide
            />
          )}
        </FieldGrid>
      </SectionCard>

      {/* ── 3. Route & terminals ───────────────────────────────────────── */}
      <SectionCard icon={MapIcon} title={t("sections.routeTerminals")}>
        <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
          {renderStop(
            1,
            Building2,
            t("route.terminalPickup"),
            t("route.kindTerminal"),
            "bg-ink",
            route.terminalPickup?.companyName,
            route.terminalPickup?.address,
            // Ref, then the slot, then the PIN — the same order as the form.
            [
              {
                label: t("route.pickupRef"),
                value: route.terminalPickup?.bookingRef,
              },
              {
                label: t("route.pickupDate"),
                value: formatDate(route.terminalPickup?.pickupDate),
              },
              {
                label: t("route.pickupTime"),
                value: route.terminalPickup?.pickupTime,
              },
              { label: t("route.pin"), value: route.terminalPickup?.pin },
            ],
            skipOf(route.terminalPickup),
          )}

          {renderStop(
            2,
            Factory,
            t("route.loadingSite"),
            t("route.kindClient"),
            "bg-copper",
            route.loadingSite?.companyName,
            route.loadingSite?.address,
            [
              {
                label: t("route.loadingSiteRef"),
                value: route.loadingSite?.bookingRef,
              },
              {
                label: t("route.onSiteDate"),
                value: formatDate(route.loadingSite?.onSiteDate),
              },
              {
                label: t("route.onSiteTime"),
                value: route.loadingSite?.onSiteTime,
              },
              { label: t("route.ramp"), value: route.loadingSite?.ramp },
              {
                label: t("route.contact"),
                value:
                  [
                    route.loadingSite?.contactName,
                    route.loadingSite?.contactPhone,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined,
              },
              { label: t("route.pin"), value: route.loadingSite?.pin },
            ],
          )}

          {renderStop(
            3,
            Anchor,
            t("route.terminalDropoff"),
            t("route.kindTerminal"),
            "bg-ink",
            route.terminalDropoff?.companyName,
            route.terminalDropoff?.address,
            [
              {
                label: t("route.deliveryRef"),
                value: route.terminalDropoff?.bookingRef,
              },
              {
                label: t("route.deliveryDate"),
                value: formatDate(route.terminalDropoff?.deliveryDate),
              },
              {
                label: t("route.deliveryTime"),
                value: route.terminalDropoff?.deliveryTime,
              },
              { label: t("route.pin"), value: route.terminalDropoff?.pin },
              {
                label: t("route.cutOffDate"),
                value: formatDate(route.terminalDropoff?.cutOffDate),
              },
              {
                label: t("route.cutOffTime"),
                value: route.terminalDropoff?.cutOffTime,
              },
            ],
            skipOf(route.terminalDropoff),
          )}

          {/* ── The stops the three anchors do not cover ──────────────────
              Rendered after the anchors rather than interleaved with them,
              because the grid is three columns wide and a route with two added
              stops would otherwise put the drop-off on a second row away from
              the terminal it pairs with. Each card says where it falls, which
              is the part the position would have told the reader. */}
          {(route.additionalStops ?? []).map((stop, index) =>
            renderStop(
              index + 4,
              Signpost,
              stop.label?.trim() ||
                t(`route.stopKinds.${stop.kind ?? "other"}`),
              t(`route.gaps.${stop.after ?? "loadingSite"}`),
              "bg-amber-600",
              stop.companyName,
              stop.address,
              [
                {
                  label: t("route.stopKind"),
                  value: t(`route.stopKinds.${stop.kind ?? "other"}`),
                },
                { label: t("route.stopRef"), value: stop.bookingRef },
                { label: t("route.stopDate"), value: formatDate(stop.date) },
                { label: t("route.stopTime"), value: stop.time },
                { label: t("route.pin"), value: stop.pin },
                { label: t("route.stopComment"), value: stop.comment },
              ],
              skipOf(stop),
            ),
          )}
        </div>
      </SectionCard>

      {/* ── 4. Pricing ─────────────────────────────────────────────────── */}
      <SectionCard icon={Wallet} title={t("shipments:pricing.title")}>
        <FieldGrid>
          <Field
            label={t("shipments:pricing.totalBuyingPrice")}
            value={
              pricing?.totalBuyingPrice != null
                ? `${pricing.totalBuyingPrice} ${pricing.currency ?? ""}`.trim()
                : undefined
            }
          />
          <Field
            label={t("shipments:pricing.totalSellingPrice")}
            value={
              pricing?.totalSellingPrice != null
                ? `${pricing.totalSellingPrice} ${pricing.currency ?? ""}`.trim()
                : undefined
            }
          />
          <Field
            label={t("shipments:pricing.pricePerKm")}
            value={pricing?.pricePerKm ?? undefined}
          />
          <Field
            label={t("shipments:pricing.mileageKm")}
            value={pricing?.mileage ?? undefined}
          />
          <Field
            label={t("shipments:pricing.currency")}
            value={pricing?.currency}
          />
        </FieldGrid>
      </SectionCard>

      {/* ── 5 & 6. Services and cost services ──────────────────────────── */}
      {/* Named for what these rows are here: the charges an invoice is built
          from. The shipment module still calls the same table "Additional
          Services" — see the `title` prop on ServicesSection. */}
      <SectionCard icon={Wrench} title={t("sections.invoicingServices")}>
        {renderServices(order.services)}
      </SectionCard>

      <SectionCard icon={Wrench} title={t("shipments:costServices.title")}>
        {renderServices(order.costServices)}
      </SectionCard>

      {/* ── 7. Attachments & notes ─────────────────────────────────────── */}
      <SectionCard icon={Paperclip} title={t("sections.attachments")}>
        <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2">
          {renderAttachments(
            t("attachments.generalLabel"),
            Paperclip,
            attachmentsByCategory("general"),
          )}
          {renderAttachments(
            t("attachments.customsLabel"),
            ShieldCheck,
            attachmentsByCategory("customs"),
          )}
        </div>

        <div className="mt-4 border-t border-[rgba(11,15,14,0.06)] pt-3">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-mist">
            {t("attachments.notesLabel")}
          </p>
          <p
            className={`mt-0.5 whitespace-pre-wrap text-[13px] ${
              order.additionalNotes ? "text-ink" : "text-secondary-300"
            }`}
          >
            {order.additionalNotes || EMPTY}
          </p>
        </div>
      </SectionCard>
    </div>
  );
}
